import { Fragment } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { GreenIconBadge } from "./GreenIconBadge";
import { ConcentricCircles } from "./ConcentricCircles";
import { TbLayoutGrid, TbCalendarCheck, TbHomeHeart } from "react-icons/tb";
import { motion, type Variants } from "framer-motion";

const MotionDiv = motion.div;

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

const headerContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.4, delayChildren: 0 } },
};

const headerItemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const steps = [
  {
    icon: TbLayoutGrid,
    label: "Step One",
    title: "Choose a service",
    description:
      "Browse our cleaning and aircon services and pick the option that best fits your home.",
  },
  {
    icon: TbCalendarCheck,
    label: "Step Two",
    title: "Pick your preferred slot",
    description:
      "Pick a date and time that suits you, then confirm your booking in a few easy steps.",
  },
  {
    icon: TbHomeHeart,
    label: "Step Three",
    title: "Enjoy a fresher home",
    description:
      "Our team handles everything with care and leaves your home feeling cleaner and more comfortable.",
  },
];

export default function HowItWorks() {
  return (
    <Box
      w="100vw"
      position="relative"
      left="50%"
      ml="-50vw"
      overflow="hidden"
      bg="greyscale.surface.default"
      pt={{ base: "2.25rem", tablet: "2.75rem", desktop: "3.25rem" }}
      pb={{ base: "4rem", tablet: "4.5rem", desktop: "5.25rem" }}
    >
      {/* CSS keyframes for flowing dash animations */}
      <style>{`
        @keyframes hiwDashH {
          from { background-position: 0 0; }
          to   { background-position: 14px 0; }
        }
        @keyframes hiwDashV {
          from { background-position: 0 0; }
          to   { background-position: 0 10px; }
        }
        .hiw-dash-h {
          animation: hiwDashH 1s linear infinite;
        }
        .hiw-dash-v {
          animation: hiwDashV 0.85s linear infinite;
        }
      `}</style>

      <ConcentricCircles corner="top-right" />

      <Box
        maxW="1280px"
        mx="auto"
        px={{ base: "1.5rem", tablet: "1.5rem", desktop: "2rem" }}
        position="relative"
        zIndex={1}
      >
        {/* Section header — consistent with ServiceHighlights */}
        <MotionDiv
          variants={headerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <Flex
            direction="column"
            align="center"
            textAlign="center"
            mb={{ base: "2.5rem", tablet: "3rem", desktop: "3.5rem" }}
          >
            <MotionDiv variants={headerItemVariants}>
              <Text
                color="primary.surface.default"
                fontWeight="800"
                letterSpacing="0.08em"
                fontSize={{
                  base: "1.2rem",
                  tablet: "1.4rem",
                  desktop: "1.5rem",
                }}
              >
                How It Works
              </Text>
              <Box
                mt="0.9rem"
                mx="auto"
                w={{ base: "60px", tablet: "78px" }}
                h="3px"
                rounded="full"
                bg="linear-gradient(90deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,1) 100%)"
              />
            </MotionDiv>

            <MotionDiv variants={headerItemVariants}>
              <Text
                mt="1rem"
                maxW="820px"
                fontSize={{
                  base: "2rem",
                  tablet: "2.5rem",
                  desktop: "3.05rem",
                }}
                fontWeight="800"
                color="greyscale.text.title"
                lineHeight="1.08"
                letterSpacing="-0.02em"
              >
                Booking made{" "}
                <Box as="span" color="primary.surface.default">
                  simple
                </Box>
              </Text>
            </MotionDiv>

            <MotionDiv variants={headerItemVariants}>
              <Text
                mt="0.95rem"
                maxW="700px"
                color="greyscale.text.body"
                fontSize={{
                  base: "0.96rem",
                  tablet: "1rem",
                  desktop: "1.02rem",
                }}
                lineHeight="1.85"
              >
                From choosing your service to enjoying a cleaner home — our
                straightforward booking process gets you set up in minutes.
              </Text>
            </MotionDiv>
          </Flex>
        </MotionDiv>

        {/* ── Desktop / Tablet: horizontal row with absolute-positioned connectors ── */}
        <Box
          display={{ base: "none", tablet: "block" }}
          position="relative"
          maxW={{ tablet: "840px", desktop: "1060px" }}
          mx="auto"
        >
          {/* Connector 1: icon 1 → icon 2 */}
          <Flex
            position="absolute"
            align="center"
            top={{ tablet: "calc(1.75rem - 3px)", desktop: "calc(2rem - 3px)" }}
            left={{
              tablet: "calc(16.67% + 2.1rem)",
              desktop: "calc(16.67% + 2.35rem)",
            }}
            right={{
              tablet: "calc(50% + 2.1rem)",
              desktop: "calc(50% + 2.35rem)",
            }}
            zIndex={2}
          >
            <MotionDiv
              initial={{ scaleX: 0, opacity: 0 }}
              whileInView={{ scaleX: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease }}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                transformOrigin: "left center",
              }}
            >
              <Box
                w="6px"
                h="6px"
                rounded="full"
                bg="rgba(34,197,94,0.65)"
                flexShrink={0}
              />
              <Box
                flex={1}
                h="2px"
                className="hiw-dash-h"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to right, rgba(34,197,94,0.45) 0px, rgba(34,197,94,0.45) 6px, transparent 6px, transparent 14px)",
                  backgroundSize: "14px 2px",
                }}
              />
              <Box
                w="6px"
                h="6px"
                rounded="full"
                bg="rgba(34,197,94,0.65)"
                flexShrink={0}
              />
            </MotionDiv>
          </Flex>

          {/* Connector 2: icon 2 → icon 3 */}
          <Flex
            position="absolute"
            align="center"
            top={{ tablet: "calc(1.75rem - 3px)", desktop: "calc(2rem - 3px)" }}
            left={{
              tablet: "calc(50% + 2.1rem)",
              desktop: "calc(50% + 2.35rem)",
            }}
            right={{
              tablet: "calc(16.67% + 2.1rem)",
              desktop: "calc(16.67% + 2.35rem)",
            }}
            zIndex={2}
          >
            <MotionDiv
              initial={{ scaleX: 0, opacity: 0 }}
              whileInView={{ scaleX: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease }}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                transformOrigin: "left center",
              }}
            >
              <Box
                w="6px"
                h="6px"
                rounded="full"
                bg="rgba(34,197,94,0.65)"
                flexShrink={0}
              />
              <Box
                flex={1}
                h="2px"
                className="hiw-dash-h"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to right, rgba(34,197,94,0.45) 0px, rgba(34,197,94,0.45) 6px, transparent 6px, transparent 14px)",
                  backgroundSize: "14px 2px",
                }}
              />
              <Box
                w="6px"
                h="6px"
                rounded="full"
                bg="rgba(34,197,94,0.65)"
                flexShrink={0}
              />
            </MotionDiv>
          </Flex>

          {/* Steps */}
          <Flex align="flex-start" position="relative" zIndex={1}>
            {steps.map((step, index) => (
              <MotionDiv
                key={step.title}
                custom={index}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                {/* Icon badge */}
                <GreenIconBadge
                  icon={step.icon}
                  outerSize={{ tablet: "3.5rem", desktop: "4rem" }}
                  iconBoxSize={{ tablet: 5, desktop: 6 }}
                  position="relative"
                  zIndex={3}
                  hover
                />

                <Text
                  mt={{ tablet: "1.6rem", desktop: "1.75rem" }}
                  fontSize="0.82rem"
                  fontWeight="800"
                  letterSpacing="0.12em"
                  textTransform="uppercase"
                  color="primary.surface.default"
                >
                  {step.label}
                </Text>

                <Text
                  mt="0.55rem"
                  fontSize={{ tablet: "1.05rem", desktop: "1.15rem" }}
                  fontWeight="800"
                  color="greyscale.text.title"
                  lineHeight="1.25"
                  letterSpacing="-0.02em"
                  maxW="14rem"
                >
                  {step.title}
                </Text>

                <Text
                  mt="0.75rem"
                  color="greyscale.text.body"
                  fontSize={{ tablet: "0.88rem", desktop: "0.92rem" }}
                  lineHeight="1.8"
                  maxW={{ tablet: "12rem", desktop: "13rem" }}
                >
                  {step.description}
                </Text>
              </MotionDiv>
            ))}
          </Flex>
        </Box>

        {/* ── Mobile: 2-column grid — icon column left, text column right ──
            The left column holds both the icon and the connector below it.
            Since the grid cell stretches to match the text row height, the
            connector fills exactly the space between one icon and the next. */}
        <MotionDiv
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.18,
              },
            },
          }}
        >
          <Box
            display={{ base: "grid", tablet: "none" }}
            gridTemplateColumns="3rem 1fr"
            columnGap="1.25rem"
          >
            {steps.map((step, index) => (
              <Fragment key={step.title}>
                {/* Left column */}
                <MotionDiv
                  variants={cardVariants}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Flex direction="column" align="center" h="100%">
                    <GreenIconBadge
                      icon={step.icon}
                      outerSize="3rem"
                      iconBoxSize={5}
                      flexShrink={0}
                      hover
                    />

                    {index < steps.length - 1 && (
                      <Flex
                        direction="column"
                        align="center"
                        flex={1}
                        pt="0.45rem"
                        pb="0.45rem"
                      >
                        <MotionDiv
                          initial={{ scaleY: 0, opacity: 0 }}
                          whileInView={{ scaleY: 1, opacity: 1 }}
                          viewport={{ once: true, amount: 0.45 }}
                          transition={{
                            duration: 0.65,
                            delay: 0.12 + index * 0.12,
                            ease,
                          }}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            height: "100%",
                            transformOrigin: "top center",
                          }}
                        >
                          <Box
                            w="6px"
                            h="6px"
                            rounded="full"
                            bg="rgba(34,197,94,0.65)"
                          />
                          <Box
                            flex={1}
                            w="2px"
                            className="hiw-dash-v"
                            style={{
                              backgroundImage:
                                "repeating-linear-gradient(to bottom, rgba(34,197,94,0.45) 0px, rgba(34,197,94,0.45) 5px, transparent 5px, transparent 10px)",
                              backgroundSize: "2px 10px",
                            }}
                          />
                          <Box
                            w="6px"
                            h="6px"
                            rounded="full"
                            bg="rgba(34,197,94,0.65)"
                          />
                        </MotionDiv>
                      </Flex>
                    )}
                  </Flex>
                </MotionDiv>

                {/* Right column */}
                <MotionDiv variants={cardVariants}>
                  <Box pb={index < steps.length - 1 ? "1.75rem" : "0"}>
                    <Text
                      fontSize="0.72rem"
                      fontWeight="800"
                      letterSpacing="0.12em"
                      textTransform="uppercase"
                      color="primary.surface.default"
                      mt="0.35rem"
                    >
                      {step.label}
                    </Text>

                    <Text
                      mt="0.35rem"
                      fontSize="1.05rem"
                      fontWeight="800"
                      color="greyscale.text.title"
                      lineHeight="1.25"
                      letterSpacing="-0.02em"
                    >
                      {step.title}
                    </Text>

                    <Text
                      mt="0.5rem"
                      color="greyscale.text.body"
                      fontSize="0.88rem"
                      lineHeight="1.75"
                    >
                      {step.description}
                    </Text>
                  </Box>
                </MotionDiv>
              </Fragment>
            ))}
          </Box>
        </MotionDiv>
      </Box>
    </Box>
  );
}
